import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebShopService } from '../../services/webShop.service';
import { IDayList, IDefineTimeService, ISelectedDay, ISelectedTime, IServiceOpenTime } from './remoteOrderGroupDefineTime.facade';
import { CoreLogService } from '../../services/log.service';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import Utils from '../../shared/utils';
import { RemoteGroupOrderService } from '../../services/RemoteGroupOrder.service';
import * as moment from "moment";
import { CreateGroupVariant } from '../../services/IRemoteGroupOrderService';
import { WebShopMode } from '../../services/IWebShopService';
import { CoreLanguageService } from '../../services/language.service';

@Injectable()
export class RemoteOrderGroupDefineTimeService implements IDefineTimeService, OnDestroy {

  private _dateListSubject: BehaviorSubject<IDayList[]> = new BehaviorSubject<IDayList[]>(null);
  private _serviceOpenTimeSubject: BehaviorSubject<IServiceOpenTime[]> = new BehaviorSubject<IServiceOpenTime[]>(null);
  private _serviceOrderTimeSubject: BehaviorSubject<IServiceOpenTime[]> = new BehaviorSubject<IServiceOpenTime[]>(null);
  private _canCreateGroupSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private _canCreateGroup: boolean = false;
  private _orderASAP: boolean = false;
  private _selectedServiceDay: ISelectedDay;
  private _selectedServiceTime: ISelectedTime;
  private _selectedOrderDay: ISelectedDay;
  private _selectedOrderTime: ISelectedTime;

  constructor(
    private _webShopService: WebShopService,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _remoteGroupOrderService: RemoteGroupOrderService,
    private _internationalisationService: InternationalisationService
  ) {
    const me = this;
    const maxCountReached = false;
    me._setCanCreateGroup(!maxCountReached);
  }

  ngOnDestroy(): void {
    // Cleanup logic if necessary
  }

  public async initial() {
    const me = this;
    await me._getOpenDays();
    await me.setAvailableTimeList()
  }

  /** Get the service ID from the web shop service */
  public get serviceID() {
    const me = this;
    return me._webShopService.getServiceMode();
  }

  /** Customer lead time specified in the portal */
  public get leadTime(): number {
    const me = this;
    const serviceLeadTime = 30;
    return serviceLeadTime;
  }

  /** Check if time selection is available in the order type settings */
  public get hasTimeSelection(): boolean {
    const me = this;
    return me._webShopService.ordertype.settingsModel.hasTimeSelection;
  }

  /** Check if the order is set to as soon as possible*/
  public get orderASAP(): boolean {
    const me = this;
    return me._webShopService.orderASAP;
  }

  /** The List of available days */
  public get dayList$(): Observable<IDayList[]> {
    const me = this;
    return me._dateListSubject.asObservable();
  }

  /** The list of service open times */
  public get serviceOpenTime$(): Observable<IServiceOpenTime[]> {
    const me = this;
    return me._serviceOpenTimeSubject.asObservable();
  }

  /** The list of service Order times */
  public get serviceOrderTime$(): Observable<IServiceOpenTime[]> {
    const me = this;
    return me._serviceOrderTimeSubject.asObservable();
  }

  /*The group creation status */
  public get canCreateGroup$(): Observable<boolean> {
    const me = this;
    return me._canCreateGroupSubject.asObservable();
  }

  /** Set the selected day (drop down) */
  public setServiceDay(day: ISelectedDay) {
    const me = this;
    if (me._selectedServiceDay !== day) {
      me._selectedServiceDay = day;
    }
  }

  /** Get the selected service day (drop down) */
  public get SelectedServiceDay(): ISelectedDay {
    const me = this;
    return me._selectedServiceDay;
  }

  /** Set the Selected service time (drop down) */
  public setSelectedServiceTime(time: ISelectedTime) {
    const me = this;
    if (me._selectedServiceTime !== time) {
      me._selectedServiceTime = time;
    }
  }

  /** Set the selected day (drop down) */
  public setOrderDay(orderDay: ISelectedDay) {
    const me = this;
    if (me._selectedOrderDay !== orderDay) {
      me._selectedOrderDay = orderDay;
    }
  }

  /** Get the selected order day (drop down) */
  public get SelectedOrderDay(): ISelectedDay {
    const me = this;
    return me._selectedOrderDay;
  }

  /** Set the order time */
  public setOrderTime(orderTime: ISelectedTime) {
    const me = this;
    if (me._selectedOrderTime !== orderTime) {
      me._selectedOrderTime = orderTime;
    }
  }

  /** set ASAP */
  public setOrderAsap(asap: boolean) {
    const me = this;
    return me._orderASAP = asap;
  }

  /** Return German key if locale is German or English key is null/empty*/
  public getLanguageKey(keyEN: string, keyDE: string): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(keyEN) ? keyDE : keyEN;
  }

  /** set service order time  */
  private _setRequestServiceTime() {
    const me = this;
    me._webShopService.orderASAP = me._orderASAP;
    me._webShopService.setServiceOrderTime(me._selectedServiceTime.timeValue)
  }

  /** set can create group*/
  private _setCanCreateGroup(canCreateGroup: boolean): void {
    const me = this;
    if (me._canCreateGroup !== canCreateGroup) {
      me._canCreateGroup = canCreateGroup;
      me._canCreateGroupSubject.next(me._canCreateGroup);
    }
  }

  public isNotNextDay(): boolean {
    if (!this.SelectedOrderDay.value) return false;
    const selectedDate = moment(this.SelectedOrderDay.value, 'ddd, DD MMM YYYY HH:mm:ss ZZ', true);
    const tomorrow = moment().add(1, 'days');
    return !selectedDate.isSame(tomorrow, 'day');
  }

  /**The list of open days */
  private async _getOpenDays() {
    const me = this;
    try {
      const openDays = await this._webShopService.getOpenDays();
      if (!Utils.IsNull(openDays) && openDays.length > 0) {
        openDays.forEach((day) => {
          me.getLanguageKey(day.keyEN, day.keyDE);
          me._dateListSubject.next(openDays);
        })
        const firstPossibleDay: ISelectedDay = { value: openDays[0].value, useTime: openDays[0].use_time }
        me.setServiceDay(firstPossibleDay);
        me.setOrderDay(firstPossibleDay);
      } else {
        // pre-orders for other days are not allowed
        // a day for today is automatically created
        me._createTodayFallback();
      }
    } catch (error) {
      me._coreLogService.error("RemoteOrderGroupDefineTimeService._getOpenDays", "get open days", error);
    }
  }

  private async useAsap() {
    const me = this;
    const webshopSettings = await me._webShopService.getWebshopSettingsDataModel();
    const deliveryGroup = me.serviceID === WebShopMode.DeliveryGroup;
    const pickupGroup = me.serviceID === WebShopMode.PickupGroup;
    if (deliveryGroup) {
      return webshopSettings.use_asap_delivery && me.isNotNextDay();
    } else if (pickupGroup) {
      return webshopSettings.use_asap_pickup && me.isNotNextDay();
    } else {
      return false;
    }
  }

  /** The list of available service times */
  public async setAvailableTimeList() {
    const me = this;
    if (me._selectedServiceDay) {
      const useAsap = await me.useAsap();
      const availableTimeItems = await this._webShopService.getAvailableServiceTimes(
        this._selectedServiceDay.value,
        this._selectedServiceDay.useTime
      );
      const serviceTimeWithLeadTime = me.filterTimesWithLeadTime(availableTimeItems, me.leadTime);
      if (useAsap) {
        // set asap time to the first available service time
        const firtsAvailableServiceTime = serviceTimeWithLeadTime[0].value;
        const ASAServiceTime: IServiceOpenTime = { display: me._coreLanguageService.value("phWebshop_AsSoonAsPossible"), value: firtsAvailableServiceTime, asap: true };
        serviceTimeWithLeadTime.unshift(ASAServiceTime);
        // set asap for the first available order time
        const firtsAvailableOrderTime = availableTimeItems[0].value;
        const ASAPOrderTime: IServiceOpenTime = { display: me._coreLanguageService.value("phWebshop_AsSoonAsPossible"), value: firtsAvailableOrderTime, asap: true };
        availableTimeItems.unshift(ASAPOrderTime);
      }
      // Pick up or delivery time
      me._serviceOpenTimeSubject.next(serviceTimeWithLeadTime);
      // Order by times
      me._serviceOrderTimeSubject.next(availableTimeItems);
    }
  }

  /** Create a fallback day item for today when pre order is not available*/
  private _createTodayFallback(): void {
    const me = this;
    const today = new Date().toISOString();
    const createTodayItem = () => {
      const todayDayItem = {
        value: today,
        key: this.getLanguageKey("Today", "Heute"),
        use_time: true,
        keyDE: this._coreLanguageService.value("phHome_Today"),
        keyEN: this._coreLanguageService.value("phHome_Today"),
        is_closed: false
      };
      this._dateListSubject.next([todayDayItem]);
      me.setServiceDay({ value: today, useTime: true });
      me.setOrderDay({ value: today, useTime: true });
    };
    createTodayItem();
    this._internationalisationService.localeChanged.subscribe(() => {
      createTodayItem();
    });
  }

  /** Filter the times based on the lead time */
  public filterTimesWithLeadTime(times: IServiceOpenTime[], leadTime: number): IServiceOpenTime[] {
    const firstTime = moment(times[0].value);
    const minOrderTime = firstTime.add(leadTime, 'minutes');
    return times.filter(time => moment(time.value).isSameOrAfter(minOrderTime));
  }

  public async createGroupResult(): Promise<CreateGroupVariant> {
    const me = this;
    me._setRequestServiceTime();
    me.setFormatedTimeValue();
    const newGroup = await me._remoteGroupOrderService.createGroup();
    if (newGroup.Variant === CreateGroupVariant.Okay) {
      return CreateGroupVariant.Okay;
    } else {
      switch (newGroup.Variant) {
        case CreateGroupVariant.isTooSoon:
          return CreateGroupVariant.isTooSoon;
        case CreateGroupVariant.TimeConflict:
          return CreateGroupVariant.TimeConflict;
        default:
          me._coreLogService.error("RemoteOrderGroupDefineTimeService.createGroup", "unknown status", newGroup.Variant);
          return CreateGroupVariant.Fail;
      }
    }
  }

  /** formated day and time value */
  public setFormatedTimeValue() {
    const me = this;
    me._remoteGroupOrderService.setSelectedServiceTime(me._formatedTime(me._selectedServiceTime.timeValue));
    me._remoteGroupOrderService.setSelectedOrderTime(me._formatedTime(me._selectedOrderTime.timeValue))
  }

  /** selected delivery / pickup day & time */
  private _formatedTime(time: string) {
    return moment(time, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ').toISOString();
  }
}